/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import LegalPageLayout from './LegalPageLayout';
import { LEGAL_OWNER } from '@/lib/legalOwner';

/**
 * Privacy Policy — GDPR-aligned notice for the SanitàNexus software.
 *
 * The licensor (Marco Panzetti) delivers the software as source code only
 * and has NO access to end-user healthcare data. Buyers (clinics) run the
 * software on their own infrastructure and act as the SOLE Data Controller
 * for any Protected Health Information (PHI) processed.
 */
const PrivacyPolicyPage = () => (
  <LegalPageLayout
    activeKey="privacy"
    title="Informativa sulla Privacy"
    subtitle="Trattamento dei dati personali ai sensi del Regolamento UE 2016/679 (GDPR) e del D.lgs. 196/2003 novellato."
    lastUpdated={LEGAL_OWNER.lastReview}
  >
    <h2>1. Titolare del Trattamento</h2>
    <p>
      Il Titolare del Trattamento dei dati personali raccolti tramite il sito vetrina
      e la landing page ufficiale del software <strong>SanitàNexus</strong> è:
    </p>
    <ul>
      <li><strong>Nome:</strong> {LEGAL_OWNER.fullName}</li>
      <li><strong>Codice Fiscale:</strong> <code>{LEGAL_OWNER.fiscalCode}</code></li>
      <li><strong>Domicilio fiscale:</strong> {LEGAL_OWNER.addressLine}</li>
      <li><strong>P.IVA:</strong> non applicabile</li>
      <li><strong>Email di contatto Privacy:</strong> <a href={`mailto:${LEGAL_OWNER.contactEmail}`}>{LEGAL_OWNER.contactEmail}</a></li>
    </ul>
    <p><em>{LEGAL_OWNER.vatExemptionNotice}</em></p>

    <h2>2. Ambito di applicazione</h2>
    <p>
      Questa informativa si applica <strong>esclusivamente</strong> ai dati personali che il Titolare
      raccoglie in prima persona attraverso il sito informativo, il flusso di licensing e la posta
      elettronica di contatto. <strong>Non si applica</strong> ai dati clinici, sanitari o amministrativi
      processati all'interno di una singola installazione del software presso una struttura sanitaria
      (clinica, studio medico, ambulatorio): tali dati sono di titolarità esclusiva della struttura
      medesima, che agisce come <strong>unico Titolare del Trattamento</strong> nei confronti dei
      propri pazienti (cfr. Sezione &ldquo;Ruolo del Titolare&rdquo;).
    </p>

    <h2>3. Ruolo del Titolare e Local-First Architecture</h2>
    <p>
      Il software <strong>SanitàNexus</strong> è distribuito con licenza d'uso ai sensi della
      <em> Legge 22 aprile 1941, n. 633</em> (Legge sul Diritto d'Autore) sotto forma di codice
      sorgente e binari eseguibili. L'installazione avviene <strong>strettamente sull'infrastruttura
      del cliente (clinica)</strong>, in modalità local-first / on-premise oppure su cloud privato
      del cliente stesso.
    </p>
    <p>
      Il Titolare (Marco Panzetti) <strong>non ha, non ha mai avuto e non avrà</strong> accesso ai
      Protected Health Information (PHI), agli identificativi dei pazienti, alle cartelle cliniche
      elettroniche (FSE), alle prescrizioni, ai referti, alle immagini diagnostiche o a qualsiasi
      altro dato sanitario processato da un'istanza del software.
    </p>

    <h2>4. Responsabile della Protezione dei Dati (DPO)</h2>
    <p>
      La designazione di un DPO ai sensi dell'Art. 37 GDPR <strong>non è applicabile</strong> al
      Titolare in questa qualità (fornitore di codice sorgente): {LEGAL_OWNER.dpoRationale}
    </p>
    <p>
      Ogni struttura sanitaria che installa e utilizza il software è invitata a designare un proprio
      DPO qualora ne ricorrano i presupposti di legge (trattamento su larga scala di categorie
      particolari di dati ex Art. 9 GDPR).
    </p>

    <h2>5. Categorie di dati trattati dal Titolare</h2>
    <p>Il Titolare tratta esclusivamente:</p>
    <ul>
      <li><strong>Dati di contatto commerciale</strong>: nome, email, ragione sociale, numero di telefono
        forniti volontariamente dai potenziali acquirenti tramite form di contatto o email diretta.</li>
      <li><strong>Dati fiscali</strong>: solo se l'acquirente sceglie di ricevere fattura o ricevuta di
        pagamento a fronte della licenza acquisita.</li>
      <li><strong>Dati tecnici di navigazione anonimi</strong>: log del webserver (indirizzo IP anonimizzato,
        user-agent, timestamp) conservati per un massimo di 6 mesi per finalità di sicurezza.</li>
    </ul>

    <h2>6. Finalità del trattamento</h2>
    <ul>
      <li>Riscontro alle richieste di informazioni commerciali e tecniche.</li>
      <li>Esecuzione del contratto di licenza (consegna del codice, supporto contrattuale).</li>
      <li>Adempimenti fiscali e contabili obbligatori.</li>
      <li>Prevenzione di abusi, frodi e attacchi informatici sul sito.</li>
    </ul>

    <h2>7. Base giuridica</h2>
    <p>
      Art. 6(1)(b) GDPR — esecuzione di un contratto o misure precontrattuali; Art. 6(1)(c) GDPR —
      obbligo legale (adempimenti fiscali); Art. 6(1)(f) GDPR — legittimo interesse alla sicurezza
      dell'infrastruttura.
    </p>

    <h2>8. Conservazione dei dati</h2>
    <p>
      I dati di contatto sono conservati per il tempo necessario a evadere la richiesta e comunque per
      un massimo di 24 mesi. I dati fiscali sono conservati per 10 anni ai sensi dell'Art. 2220 c.c.
      I log tecnici sono cancellati entro 6 mesi.
    </p>

    <h2>9. Diritti dell'interessato</h2>
    <p>
      Ai sensi degli Artt. 15–22 GDPR l'interessato ha diritto di accedere ai propri dati, rettificarli,
      cancellarli, limitarne il trattamento, opporsi al trattamento e ottenere la portabilità.
      Le richieste vanno inviate a <a href={`mailto:${LEGAL_OWNER.contactEmail}`}>{LEGAL_OWNER.contactEmail}</a>.
      Il Titolare risponde entro 30 giorni. L'interessato può inoltre proporre reclamo al Garante per la
      Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it">garanteprivacy.it</a>).
    </p>

    <h2>10. Trasferimenti extra-UE</h2>
    <p>
      Nessun trasferimento sistematico verso Paesi extra-SEE. Eventuali trasferimenti occasionali
      (es. uso di servizi email) avvengono verso soggetti aderenti al <em>EU-US Data Privacy Framework</em>
      o con adozione di Clausole Contrattuali Standard.
    </p>

    <h2>11. Modifiche</h2>
    <p>
      La presente informativa può essere aggiornata; la versione corrente è sempre disponibile su
      <code> /legal/privacy</code>. Ultima revisione: <strong>{LEGAL_OWNER.lastReview}</strong>.
    </p>
  </LegalPageLayout>
);

export default PrivacyPolicyPage;
